<!-- Orbi "Find a Bill" card (PCSA-13419). The Figma draws two states in one card
     — the search form, and the lookup failure — and they REPLACE each other:
     neither error frame shows any field. The form group is not rebuilt when the
     error shows, so every entry survives and "Go Back to Review" returns to the
     filled-in form (AC 9).

     Copy, field order, validators and failure text all come from the portal's own
     registered-user bill lookup (`PortalDashboardInvoicesSearch`) rather than
     from mobile, which has no Find a Bill card at all. -->
<section class="orbi-find-a-bill-card" aria-label="Find a bill">
  <div class="orbi-find-a-bill-card__card">
    <div class="orbi-find-a-bill-card__head">
      <span class="material-icons-round orbi-find-a-bill-card__icon" aria-hidden="true">credit_card</span>
      <h3 class="orbi-find-a-bill-card__title">{{ title }}</h3>
    </div>

    <!-- SEARCH STATE ------------------------------------------------------- -->
    <ng-container *ngIf="!errorCondition">
      <p class="orbi-find-a-bill-card__instructions">{{ instructions }}</p>

      <form class="orbi-find-a-bill-card__form" [formGroup]="searchForm" novalidate>
        <!-- Invoice Number — prefilled from the turn's `data.invoices` when the
             agent sent one, and editable either way (AC 4). No `size` on any of
             the three fields: the Figma draws them as one column of EQUAL boxes,
             at one length that is the field's, not the content's — capped in the
             wide frames and filling the card in the narrow one, which is the
             SCSS's business (`&__field`, QA 08/21). -->
        <div class="orbi-find-a-bill-card__field">
          <label for="orbi-find-a-bill-invoice">{{ invoiceLabel }}</label>
          <input
            id="orbi-find-a-bill-invoice"
            type="text"
            #invoiceNumber
            (blur)="invoiceNumber.value = invoiceNumber.value.trim()"
            class="form-control"
            [ngClass]="{
              'is-invalid': formControls.invoiceNumber.invalid && (formControls.invoiceNumber.dirty || formControls.invoiceNumber.touched)
            }"
            formControlName="invoiceNumber"
          />
          <ui-form-field-msg
            id="orbi-find-a-bill-invoice-error"
            [validationMessages]="validationMessages.invoiceNumber"
            controlName="invoiceNumber"
          ></ui-form-field-msg>
        </div>

        <!-- ZIP Code — masked to 5 digits, the same control the Billing page uses.
             `maxlength` + `mask` state what the field ACCEPTS; how long the box is
             drawn is the SCSS's business, and the Figma draws it the same length as
             the other two rather than sized to five characters. -->
        <div class="orbi-find-a-bill-card__field">
          <label for="orbi-find-a-bill-zip">{{ zipLabel }}</label>
          <input
            id="orbi-find-a-bill-zip"
            type="text"
            autocomplete="off"
            #zip
            (blur)="zip.value = zip.value.trim()"
            class="form-control"
            [ngClass]="{ 'is-invalid': formControls.zipCode.invalid && (formControls.zipCode.dirty || formControls.zipCode.touched) }"
            formControlName="zipCode"
            maxlength="5"
            mask="00000"
            [showMaskTyped]="false"
            [validation]="false"
          />
          <ui-form-field-msg
            id="orbi-find-a-bill-zip-error"
            [validationMessages]="validationMessages.zipCode"
            controlName="zipCode"
          ></ui-form-field-msg>
        </div>

        <!-- Date of Birth — mm/dd/yyyy mask with the shown placeholder, so the
             expected format is visible before typing (Billing page parity). -->
        <div class="orbi-find-a-bill-card__field">
          <label for="orbi-find-a-bill-dob">{{ dobLabel }}</label>
          <input
            id="orbi-find-a-bill-dob"
            type="text"
            class="form-control"
            mask="00/00/0000"
            [showMaskTyped]="true"
            [ngClass]="{
              'is-invalid': formControls.dateOfBirth.invalid && (formControls.dateOfBirth.dirty || formControls.dateOfBirth.touched)
            }"
            formControlName="dateOfBirth"
          />
          <ui-form-field-msg
            id="orbi-find-a-bill-dob-error"
            [validationMessages]="validationMessages.dateOfBirth"
            controlName="dateOfBirth"
          ></ui-form-field-msg>
        </div>

        <!-- Both actions (AC 6). `buttonDisabled` is `ui-button`'s own input —
             the component exposes no `disabled`, so binding that name would be
             an inert unknown property rather than a guard — and it withholds
             only the in-flight lookup. An incomplete form must NOT disable
             these: AC 7 requires a click with missing criteria to reach
             `onSubmit`, which marks every control so each missing field states
             what it needs and sends no request. A disabled button would swallow
             the click and leave the user with no explanation.

             No `minWidth` override: the Figma's wide pills measure 128px and
             136px, which ARE `ui-button`'s own 128px floor and the natural
             `btn-sm` width of "Update Insurance" — the drawn sizes are the
             library's defaults, so overriding either would move them off spec. -->
        <div class="orbi-find-a-bill-card__actions">
          <ui-button
            class="orbi-find-a-bill-card__btn"
            [buttonText]="payLabel"
            rank="primary"
            size="small"
            [buttonDisabled]="isSearching"
            [inProgress]="payInProgress"
            (handleClick)="onSubmit('payMyBill')"
          ></ui-button>
          <ui-button
            class="orbi-find-a-bill-card__btn"
            [buttonText]="insuranceLabel"
            rank="secondary"
            size="small"
            [buttonDisabled]="isSearching"
            [inProgress]="insuranceInProgress"
            (handleClick)="onSubmit('updateInsurance')"
          ></ui-button>
        </div>
      </form>
    </ng-container>

    <!-- FAILURE STATE ------------------------------------------------------ -->
    <ng-container *ngIf="errorCondition">
      <!-- `ui-alert` (CLAUDE.md) renders the red glyph block + hairline the Figma
           draws. `fullWidth` is deliberately OMITTED (PCSA-13312, 08/14 Vibe:
           "hug the error message to its content rather than full-length; applies
           to all error messages"): `ui-alert`'s root is already
           `d-inline-flex`, and `fullWidth` is the single thing that adds `w-100`
           to it, so dropping the input is the whole fix. The card is a column
           flex container, so `align-self: flex-start` in the SCSS stops the
           cross-axis stretch that would otherwise re-expand it. -->
      <ui-alert class="orbi-find-a-bill-card__alert" alertStatus="danger" [alertTextTemplate]="alertText"></ui-alert>
      <ng-template #alertText>
        <div class="alert-content orbi-find-a-bill-card__alert-text">{{ alertHeading }}</div>
      </ng-template>

      <!-- The bulleted causes belong to the criteria failure only: a technical
           failure or a settled balance has nothing to enumerate. -->
      <ng-container *ngIf="errorCondition === 'searchCriteria'">
        <p class="orbi-find-a-bill-card__error-intro">{{ errorIntro }}</p>
        <ul class="orbi-find-a-bill-card__error-reasons">
          <li *ngFor="let reason of errorReasons">{{ reason }}</li>
        </ul>
        <!-- The billing number is a `tel:` link at EVERY width (PCSA-13562).
             PCSA-13312 shipped it as a responsive pair — a plain span from md up,
             an anchor below — reading the 08/14 Vibe note ("clickable on mobile
             web") as a mobile-only ask, and copying the idiom the portal uses for
             this same number (billing-help-conent). QA raised it back: on desktop
             the number reads as dead plain text, and the Figma draws it as a link
             there too. A desktop `tel:` is not the dead end that pair assumed —
             the OS hands it to whatever registered the handler (Handoff/FaceTime,
             Teams, Skype, Phone Link) — and a touch laptop is "desktop" by
             breakpoint anyway. One anchor, no `d-*` gating, and the plain-text
             twin is gone rather than left to drift from the link's text. -->
        <p class="orbi-find-a-bill-card__error-contact">
          {{ errorContactBefore }}
          <a [href]="errorContactPhoneHref" class="orbi-find-a-bill-card__error-phone text-decoration-underline">{{ errorContactPhone }}</a>
          {{ errorContactAfter }}
        </p>
      </ng-container>

      <!-- Back to the form with everything the user typed still in it (AC 9).
           The leading arrow is the Figma's; `iconLeft` is `ui-button`'s own
           leading-glyph slot, so it needs no markup here. -->
      <div class="orbi-find-a-bill-card__actions">
        <ui-button
          class="orbi-find-a-bill-card__btn"
          [buttonText]="errorDismissLabel"
          rank="primary"
          size="small"
          iconLeft="arrow_back"
          (handleClick)="onDismissError()"
        ></ui-button>
      </div>
    </ng-container>
  </div>
</section>
